export interface DomainConfig {
  domainName: string;
  tld: string;
  displayName: string;
  status: string;
  minimumOfferUsd: number;
  askingPriceUsd?: number;
  currency: string;
  /**
   * Toggle the acquisition inquiry mechanism:
   * - true: Interactive web form with Cloudflare Turnstile and serverless email delivery
   * - false: Direct email address link and mailto contact card
   */
  enableOfferForm: boolean;
  contactEmail: string;
  turnstileSiteKey: string;
  gaMeasurementId: string;
  escrowPartners: Array<{
    name: string;
    description: string;
    url?: string;
  }>;
  sellerEmailFallback: string;
  features: Array<{
    title: string;
    description: string;
    icon: string;
  }>;
  useCases: Array<{
    title: string;
    category: string;
    description: string;
    potential: string;
  }>;
}

export const DOMAIN_CONFIG: DomainConfig = {
  domainName: "service-now.dev",
  tld: ".dev",
  displayName: "service-now.dev",
  status: "Assessing Market Interest",
  minimumOfferUsd: 2500,
  currency: "USD",
  // Set to false to disable the form and display a direct email inquiry card instead:
  enableOfferForm: (import.meta as any).env?.VITE_ENABLE_OFFER_FORM !== undefined
    ? (import.meta as any).env.VITE_ENABLE_OFFER_FORM !== 'false'
    : true,
  contactEmail: (import.meta as any).env?.VITE_CONTACT_EMAIL || "seller@service-now.dev",
  turnstileSiteKey: (import.meta as any).env?.VITE_TURNSTILE_SITE_KEY || "1x00000000000000000000AA",
  gaMeasurementId: (import.meta as any).env?.VITE_GA_MEASUREMENT_ID || "",
  sellerEmailFallback: "seller@service-now.dev",
  escrowPartners: [
    {
      name: "Escrow.com",
      description: "Priceless peace of mind with the world's most trusted online escrow service (licensed & audited).",
    },
    {
      name: "Sedo",
      description: "Direct transfer concierge and premier marketplace escrow protection.",
    },
    {
      name: "Dan.com",
      description: "Fast, automated domain checkout and instant registrar push support.",
    },
  ],
  features: [
    {
      title: "Authoritative .dev TLD",
      description: "The dedicated, globally recognized top-level domain for software developers, engineers, and technical innovators with mandatory HTTPS security.",
      icon: "Code2",
    },
    {
      title: "Action-Oriented Domain Brand",
      description: "'Service-Now' conveys instant availability, on-demand IT delivery, and real-time responsiveness that enterprise technology buyers prioritize.",
      icon: "ShieldCheck",
    },
    {
      title: "High Type-In & Organic Affinity",
      description: "Instant brand recognition for software engineers, IT leaders, and cloud architects seeking on-demand developer services and consulting.",
      icon: "Search",
    },
    {
      title: "High-Margin Enterprise Services",
      description: "Enterprise IT consulting, cloud services, and managed operations command hundreds of billions in global enterprise budgets annually.",
      icon: "TrendingUp",
    },
  ],
  useCases: [
    {
      title: "Cloud & IT Services Consultancy",
      category: "Professional Services",
      description: "Differentiate your specialized technology advisory practice with an authoritative, developer-centric digital presence that commands client trust.",
      potential: "High-Value Enterprise Retainers",
    },
    {
      title: "On-Demand Microservices & API Hub",
      category: "SaaS & Platform",
      description: "Build an API marketplace, microservice catalog, or automated developer tooling suite providing instant cloud infrastructure services.",
      potential: "Scalable Recurring SaaS Revenue",
    },
    {
      title: "DevOps & Cloud Engineering Hub",
      category: "Engineering & Tools",
      description: "Create the go-to technical resource, automation toolkit, and developer community for modern IT operations and cloud service management.",
      potential: "Rapid Community & Tool Adoption",
    },
    {
      title: "Technical Training & Architecture Academy",
      category: "Education & Upskilling",
      description: "Host premier systems architecture bootcamps, hands-on engineering labs, and certification programs for the global developer workforce.",
      potential: "Scalable Course & Cohort Sales",
    },
  ],
};
